interface AwsWafIntegration {
  getToken(): Promise<string>;
}

declare global {
  interface Window {
    AwsWafIntegration?: AwsWafIntegration;
  }
}

export const WAF_SDK_URL = process.env.NEXT_PUBLIC_AWS_WAF_SDK_URL;

const SDK_POLL_INTERVAL_MS = 50;
const SDK_WAIT_TIMEOUT_MS = 3000;

let sdkWait: Promise<AwsWafIntegration | undefined> | undefined;

// challenge.js defines AwsWafIntegration asynchronously after it loads; memoised so a blocked script only delays the first request.
function waitForSdk(): Promise<AwsWafIntegration | undefined> {
  sdkWait ??= new Promise((resolve) => {
    const deadline = Date.now() + SDK_WAIT_TIMEOUT_MS;
    const poll = () => {
      if (window.AwsWafIntegration || Date.now() >= deadline) {
        resolve(window.AwsWafIntegration);
      } else {
        setTimeout(poll, SDK_POLL_INTERVAL_MS);
      }
    };
    poll();
  });
  return sdkWait;
}

async function getWafToken(): Promise<string | undefined> {
  const sdk = window.AwsWafIntegration ?? (await waitForSdk());
  try {
    return await sdk?.getToken();
  } catch {
    // Fail open: the WAF decides whether a tokenless request is allowed.
    return undefined;
  }
}

export async function wafFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  if (typeof window === "undefined" || !WAF_SDK_URL) return fetch(input, init);

  const token = await getWafToken();
  if (!token) return fetch(input, init);

  const headers = new Headers(init?.headers);
  headers.set("x-aws-waf-token", token);
  return fetch(input, { ...init, headers });
}
